import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowDown, Layers, Languages, UserPlus, HeartHandshake,
  FileText, SlidersHorizontal, Share2, Send,
} from "lucide-react";
import {
  MarketingHeader, MarketingFooter, Primary,
} from "../components/layout/MarketingShell";
import BicarraVideo, { useSiteLang } from "../components/sections/BicarraVideo";

/* The landing page leads with the thing hardest for anyone else to copy:
   Bicarra is not tied to one AI vendor. Then the everyday reasons a
   Malaysian business would choose it.

   Every figure here is checked. 446 models from 61 labs were live on
   OpenRouter the day this was written, so "400+" and "60+" stay true as
   that catalogue moves. No customer counts, no message volumes, no
   uptime promises — we do not have those numbers.

   Deliberately absent: any claim about where data is stored. The database
   and uploaded files do sit on the Kuala Lumpur server, but backups
   currently go to a US region, so the claim is not yet clean enough to
   make in public. */

const STEPS = [
  { n: "01", icon: FileText, title: "Add your information",
    body: "Share your website, documents, or common questions." },
  { n: "02", icon: SlidersHorizontal, title: "Choose your model",
    body: "Bring your own API key, or a model you run yourself." },
  { n: "03", icon: Share2, title: "Share your assistant",
    body: "Add it to your website and start helping customers." },
];

/* Ranked. The order on screen is the order of the argument. */
const REASONS = [
  { icon: Layers, title: "400+ models, or your own",
    body: "OpenAI, Anthropic, Google, xAI, DeepSeek, Groq, Together and 1min.AI directly, or 400+ models from 60+ labs through one OpenRouter key. Point it at Ollama, LM Studio or vLLM instead and the conversation never leaves your own hardware." },
  { icon: Languages, title: "Speaks Bahasa Melayu",
    body: "Answers your customers in Bahasa Melayu and English, not a translation of an English script." },
  { icon: UserPlus, title: "Captures leads, not just chats",
    body: "A conversation can become a contact in your list, with the chat history attached to it." },
  { icon: HeartHandshake, title: "Adab & akhlak, built in",
    body: "Respectful language and considered replies, as the default rather than a setting." },
];

const CHAT = [
  { from: "them", text: "Hi, kedai buka hari Ahad?", at: "10:24 AM" },
  { from: "bot", text: "Ya, kami buka pada hari Ahad, 10 pagi hingga 6 petang. Ada apa-apa yang boleh saya bantu?", at: "10:24 AM" },
  { from: "them", text: "Boleh saya tempah masa?", at: "10:25 AM" },
  { from: "bot", text: "Boleh. Hari apa paling sesuai untuk anda?", at: "10:25 AM" },
];

const MODELS = ["OpenAI", "Anthropic", "Google", "DeepSeek", "Groq", "Your own model"];


const VIDEO_COPY = {
  en: { intro: "See Bicarra in action", tutorial: "Watch it step by step",
        tutorialBody: "From creating your account to the chat bubble on your website, in about three minutes." },
  ms: { intro: "Lihat Bicarra beraksi", tutorial: "Tonton langkah demi langkah",
        tutorialBody: "Daripada mencipta akaun hingga gelembung sembang di laman web anda, dalam kira-kira tiga minit." },
};

export default function Landing() {
  const [lang] = useSiteLang();
  const V = VIDEO_COPY[lang];
  const { hash } = useLocation();

  /* The sections only exist once React mounts, so the browser cannot
     honour /#how or /#tutorial on first load by itself. */
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ block: "start" });
  }, [hash]);

  return (
    <div className="b2 min-h-screen bg-b2-canvas" data-testid="landing-page">
      <MarketingHeader />

      {/* -------------------------------- hero ------------------------------- */}
      <section className="max-w-[1120px] mx-auto px-6 pt-16 pb-12 text-center">
        <p className="text-[12px] font-semibold tracking-[0.18em] uppercase text-b2-indigo">
          Made for Malaysian businesses
        </p>
        <h1 className="mt-5 text-[44px] sm:text-[58px] leading-[1.06] font-bold tracking-[-0.035em] text-b2-ink">
          One assistant.<br />Almost any AI model.
        </h1>
        <p className="mt-6 mx-auto max-w-[620px] text-[17px] leading-relaxed text-b2-muted">
          Connect Bicarra to 400+ models from 60+ AI labs — or run your own model,
          so your conversations never leave your hardware.
        </p>
        <div className="mt-9 flex items-center justify-center gap-6 flex-wrap">
          <Primary to="/register" testid="hero-get-started">Get started</Primary>
          <a href="#how" className="inline-flex items-center gap-1.5 text-[15px] font-medium text-b2-indigo hover:underline">
            See how it works <ArrowDown className="w-4 h-4" strokeWidth={1.9} />
          </a>
        </div>
        <p className="mt-5 text-[13px] text-b2-faint">No coding needed.</p>
      </section>

      {/* ---------------------------- intro video ---------------------------- */}
      <section id="video" className="max-w-[880px] mx-auto px-6 pb-24 scroll-mt-24">
        <h2 className="sr-only">{V.intro}</h2>
        <BicarraVideo kind="intro" />
      </section>

      {/* ----------------------------- hero panel ---------------------------- */}
      <section className="max-w-[1120px] mx-auto px-6 pb-24">
        <div className="rounded-[20px] bg-b2-wash px-7 py-10 sm:px-12 sm:py-12 grid gap-10 lg:grid-cols-[1fr_1.05fr] items-center">
          <div>
            <h2 className="text-[30px] sm:text-[34px] leading-[1.12] font-bold tracking-[-0.03em] text-b2-ink">
              Your business.<br />Always ready to help.
            </h2>
            <p className="mt-4 text-[15.5px] leading-relaxed text-b2-inksoft max-w-[380px]">
              Give Bicarra your business information. Let it handle the everyday
              questions, on whichever model you choose.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {MODELS.map((m) => (
                <span key={m}
                      className="h-8 px-3.5 rounded-full bg-white/80 text-b2-washink text-[13px] font-medium inline-flex items-center">
                  {m}
                </span>
              ))}
            </div>
          </div>

          {/* A worked example, labelled as one. */}
          <div className="rounded-[16px] bg-white border border-black/[0.04] overflow-hidden">
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-b2-line">
              <img src="/brand/bicarra-icon.svg" alt="" className="w-7 h-7 rounded-[8px]" />
              <div className="leading-tight">
                <p className="text-[13.5px] font-semibold text-b2-ink">Bicarra</p>
                <p className="text-[11.5px] text-b2-faint">Example conversation</p>
              </div>
            </div>
            <div className="px-4 py-4 space-y-3">
              {CHAT.map((m, i) => (
                <div key={i} className={`flex ${m.from === "them" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] px-3.5 py-2.5 rounded-[13px] ${
                    m.from === "them" ? "bg-b2-wash text-b2-ink" : "bg-black/[0.04] text-b2-ink"
                  }`}>
                    <p className="text-[13.5px] leading-relaxed">{m.text}</p>
                    <p className="mt-1 text-[10.5px] text-b2-faint">{m.at}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 pb-4 flex items-center gap-2">
              <div className="flex-1 h-10 rounded-[10px] border border-b2-line flex items-center px-3.5 text-[13.5px] text-b2-faint">
                Type your message…
              </div>
              <span className="w-10 h-10 rounded-[10px] bg-b2-indigo text-white flex items-center justify-center shrink-0">
                <Send className="w-4 h-4" strokeWidth={2} />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------- steps ------------------------------- */}
      <section id="how" className="max-w-[1120px] mx-auto px-6 pb-24 scroll-mt-24">
        <h2 className="text-center text-[30px] sm:text-[34px] font-bold tracking-[-0.03em] text-b2-ink">
          A little setup. A lot of help.
        </h2>
        <div className="mt-12 grid gap-10 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <div key={s.n} className={i > 0 ? "sm:pl-10 sm:border-l sm:border-b2-line" : ""}>
              <span className="w-12 h-12 rounded-full bg-b2-wash text-b2-washink flex items-center justify-center">
                <s.icon className="w-5 h-5" strokeWidth={1.8} />
              </span>
              <p className="mt-6 text-[13px] font-semibold text-b2-indigo tabular-nums">{s.n}</p>
              <h3 className="mt-2 text-[17px] font-bold tracking-[-0.02em] text-b2-ink">{s.title}</h3>
              <p className="mt-2 text-[14.5px] leading-relaxed text-b2-muted">{s.body}</p>
            </div>
          ))}
        </div>

        {/* The full walkthrough sits with the three steps it demonstrates. */}
        <div id="tutorial" className="mt-16 rounded-[20px] bg-b2-wash px-6 py-8 sm:px-10 sm:py-10 scroll-mt-24">
          <h3 className="text-[22px] sm:text-[24px] font-bold tracking-[-0.025em] text-b2-ink">{V.tutorial}</h3>
          <p className="mt-2 mb-6 text-[15px] leading-relaxed text-b2-inksoft max-w-[560px]">{V.tutorialBody}</p>
          <BicarraVideo kind="tutorial" showChapters />
        </div>
      </section>

      {/* ------------------------------ reasons ------------------------------ */}
      <section className="max-w-[1120px] mx-auto px-6 pb-24">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] items-start">
          <div className="lg:sticky lg:top-28">
            <h2 className="text-[30px] sm:text-[34px] leading-[1.12] font-bold tracking-[-0.03em] text-b2-ink">
              Not tied to one<br />AI company.
            </h2>
            <p className="mt-4 text-[15.5px] leading-relaxed text-b2-muted max-w-[360px]">
              Most chatbots pick a model for you and hope you never ask.
              Bicarra lets you choose, change your mind, and take the whole
              thing in-house when you are ready.
            </p>
          </div>

          <div className="divide-y divide-b2-line">
            {REASONS.map((r) => (
              <div key={r.title} className="flex gap-5 py-6 first:pt-0">
                <span className="w-11 h-11 rounded-[12px] bg-b2-wash text-b2-washink flex items-center justify-center shrink-0">
                  <r.icon className="w-[19px] h-[19px]" strokeWidth={1.8} />
                </span>
                <div>
                  <h3 className="text-[16.5px] font-bold tracking-[-0.02em] text-b2-ink">{r.title}</h3>
                  <p className="mt-1.5 text-[14.5px] leading-relaxed text-b2-muted">{r.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------ cta band ----------------------------- */}
      <section className="max-w-[1120px] mx-auto px-6 pb-20">
        <div className="rounded-[20px] bg-b2-wash px-8 py-16 text-center">
          <h2 className="text-[30px] sm:text-[34px] font-bold tracking-[-0.03em] text-b2-ink">
            More time for your business.
          </h2>
          <p className="mt-3 text-[15.5px] text-b2-inksoft">
            Let Bicarra take care of the everyday questions.
          </p>
          <div className="mt-8">
            <Primary to="/register" testid="cta-get-started">Get started</Primary>
          </div>
          <Link to="/contact-us" className="mt-5 inline-block text-[14px] text-b2-indigo hover:underline">
            Have questions? Talk to us
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
